/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-hooks/exhaustive-deps */

import ExportPopover from '@/components/DashboardCard/components/Header/components/ExportPopover';
import { Presentation } from '@/components/PresentationButton';
import SimpleHeader from '@/components/SimpleHeader';
import constants from '@/Constants';
import { usePresentation } from '@/hooks/Presentation';
import { colors } from '@/theme/theme/colors';
import { useToast } from '@chakra-ui/react';
import ContainedLoading from '@components/ContainedLoading';
import DashboardCard from '@components/DashboardCard';
import DashMenu from '@components/DashMenu';
import html2canvas, { Options } from 'html2canvas';
import jsPDF, { jsPDFOptions } from 'jspdf';
import React, { useCallback, useMemo, useRef } from 'react';
import { Responsive } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized-auto-sizer';

import FilterRow from './components/FilterRow';
import { useScreenshotContext } from './contexts/ScreenshotContext';
import { DashboardRenderProps, DashCard } from './interfaces';
import { Container, Content, EmptyData, HeaderContainer } from './styles';

const MainScreen: React.FC<DashboardRenderProps> = ({
  dashId,
  cards,
  filters,
  dashMenuOptions,
  dashMenuPath,
  useDashMenu,
  isMobile,
  formattedFilters,
  hiddenFilter,
  cancelToken,
  filterPanel,
  navigationPanel,
  refreshCardTimout,
  hiddenSupergroup,
  currentSuperGroup,
  openSelectSuperGroup,
  initialFilters,
  getFormattedFilter,
  filterLayout,
  backgroundColor,
  borderColor,
  ...rest
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const toast = useToast();
  const { t } = useTranslation();
  const { isPresenting } = usePresentation();
  const { setCapturingScreenshot } = useScreenshotContext();

  const generateCards = useCallback(
    () =>
      cards &&
      Object.values(cards).map((card: DashCard) => {
        const parsedCard = filters
          ? {
              ...card,
              url: card.url.split('&filter')[0],
            }
          : { ...card };
        return (
          <DashboardCard
            key={card.id}
            filters={filters}
            cancelToken={cancelToken}
            filterPanel={filterPanel}
            navigationPanel={navigationPanel}
            refreshCardTimout={refreshCardTimout}
            formattedFilters={formattedFilters}
            initialFilters={initialFilters}
            dashId={dashId}
            dashMenuOptions={dashMenuOptions}
            onMenu={useDashMenu?.state?.show}
            setFilter={rest?.onFilter}
            {...parsedCard}
            style={{
              borderRadius: '5px',
              border: `1px solid ${borderColor || '#eff6ef'}`,
              padding: '16px 16px 16px 16px',
              fontFamily:
                'ui-sans-serif,system-ui,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji',
            }}
          />
        );
      }),
    [cards, filters, useDashMenu],
  );

  const filterButtonStyleMobile = {
    marginRight: '60px',
    width: 'auto',
  };
  const filterButtonStyle = {
    width: 'auto',
  };

  const MemorizedCards = useMemo(() => {
    return generateCards();
  }, [generateCards]);

  const downloadDashboard = useCallback(
    async (exportType) => {
      if (headerRef.current && contentRef.current) {
        const imagesExportTypes = ['jpeg', 'png'];
        const exportAsImage = imagesExportTypes.includes(exportType);

        // Set capturing state to trigger UI changes (hide buttons, disable virtualization)
        setCapturingScreenshot(true);

        // Wait for React to re-render components
        await new Promise((resolve) => setTimeout(resolve, 300));

        const options: Partial<Options> = {
          allowTaint: true,
          useCORS: true,
          scale: 3,
        };

        const headerCanvas = await html2canvas(headerRef.current, options);
        const contentCanvas = await html2canvas(contentRef.current, options);
        const headerHeight = headerCanvas.height;
        const canvasWidth = headerCanvas.width;

        const link = document.createElement('a');
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d');

        if (!canvasContext) {
          toast({
            status: 'error',
            title: t('common.error'),
            description: t('messageEdit.fileDownloadError'),
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        canvas.height = contentCanvas.height + headerCanvas.height;
        canvas.width = canvasWidth;
        canvasContext.drawImage(headerCanvas, 0, 0);
        canvasContext.drawImage(contentCanvas, 0, headerHeight);

        link.href = canvasContext.canvas.toDataURL(
          `image/${exportAsImage ? exportType : 'jpeg'}`,
          1,
        );

        if (exportAsImage) {
          link.download = `${rest.dashName ?? 'dashboard'}.${exportType}`;
          link.click();
        } else if (exportType === 'pdf') {
          const orientation: jsPDFOptions['orientation'] =
            canvas.height > canvasWidth ? 'portrait' : 'landscape';

          const pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [canvasWidth, canvas.height],
          });

          const pdfSizes = pdf.internal.pageSize;

          pdf.addImage(
            link.href,
            'JPEG',
            0,
            0,
            pdfSizes.width,
            pdfSizes.height,
          );
          pdf.save(`${rest.dashName ?? 'dashboard'}.pdf`);
        }
        toast({
          status: 'success',
          title: t('common.success'),
          description: t('messageEdit.fileDownloadSuccess'),
          duration: 3000,
          isClosable: true,
        });
        canvas.remove();

        setCapturingScreenshot(false);
      }
    },
    [contentRef, rest, setCapturingScreenshot],
  );

  const btnsList: any[] = useMemo(() => {
    const {
      filterEndpoint,
      onFilter,
      openFilters,
      MenuFilterComponent,
      dashActions,
    } = rest;

    return filterLayout
      ? [
          {
            key: 'presentation',
            show: dashActions && dashActions?.canPresent && !isMobile,
            Component: Presentation,
          },
          {
            key: 'export-1',
            show:
              dashActions && dashActions?.canTakeScreenshot && !isPresenting,
            Component: ExportPopover,
            props: {
              exportXlsx: () => {},
              exportImage: downloadDashboard,
              exportTypes: ['jpeg', 'png', 'pdf'],
              buttonClasses: `downloadButton ${
                isMobile && 'downloadButtonMobile'
              }`,
            },
          },
          {
            key: 'supergroup',
            callback: () => openSelectSuperGroup(),
            title: currentSuperGroup.name,
            className: 'lighterButton',
            show: !hiddenSupergroup && !isPresenting,
          },
          {
            key: 'filter',
            title: 'buttons.open_filters',
            Component: MenuFilterComponent,
            show: !hiddenFilter && !isPresenting,
            style: isMobile ? filterButtonStyleMobile : filterButtonStyle,
            props: {
              onSubmit: onFilter,
              screen: filterEndpoint,
              initialFilters,
              getFormattedFilter,
              isMobile,
            },
          },
        ]
      : [
          {
            key: 'presentation',
            show: dashActions && dashActions?.canPresent && !isMobile,
            Component: Presentation,
          },
          {
            key: 'export-2',
            show:
              dashActions && dashActions?.canTakeScreenshot && !isPresenting,
            Component: ExportPopover,
            props: {
              exportXlsx: () => {},
              exportImage: downloadDashboard,
              exportTypes: ['jpeg', 'png', 'pdf'],
              buttonClasses: `downloadButton ${
                isMobile && 'downloadButtonMobile'
              }`,
            },
          },
          {
            key: 'supergroup',
            callback: () => openSelectSuperGroup(),
            title: currentSuperGroup.name,
            className: 'lighterButton',
            show: !hiddenSupergroup && !isPresenting,
          },
          {
            key: 'filter',
            callback: () => openFilters(),
            title: 'buttons.open_filters',
            show: !hiddenFilter && !isPresenting,
            style: isMobile ? filterButtonStyleMobile : filterButtonStyle,
          },
        ];
  }, [
    rest.filterEndpoint,
    rest.onFilter,
    rest.openFilters,
    rest.MenuFilterComponent,
    rest.dashActions,
    isMobile,
  ]);

  const headerRender = useCallback(() => {
    return (
      <HeaderContainer
        havePictureLogo={!!rest?.dashPictureLogo}
        ref={headerRef}
      >
        <SimpleHeader
          title={rest.dashName}
          pictureLogo={rest.dashPictureLogo}
          isMobile={isMobile}
          buttons={btnsList.filter((el: any) => el.show)}
          dashInfo={rest.dashActions?.dashInfo}
        />
        {filterLayout && (
          <FilterRow
            onSubmit={rest.onFilter}
            screen={rest.filterEndpoint}
            initialFilters={initialFilters}
            getFormattedFilter={getFormattedFilter}
          />
        )}
      </HeaderContainer>
    );
  }, [
    rest,
    isMobile,
    btnsList,
    filterLayout,
    initialFilters,
    getFormattedFilter,
  ]);

  const dashRender = useCallback(() => {
    const {
      dashName,
      mounted,
      layouts,
      gridProps,
      withoutDashs,
      onBreakpointChange,
      onLayoutChange,
      isLoadingContent,
      enableScrollBar,
    } = rest;

    if (withoutDashs) return <EmptyData>Sem Dados</EmptyData>;

    if (isLoadingContent || !dashName)
      return <ContainedLoading label="" allHeight hasIcon />;
    return (
      <Content className="content" isMobile={isMobile}>
        <AutoSizer>
          {({ width, height }) => (
            <div
              className={enableScrollBar ? '' : 'no-scrollbar'}
              style={{
                width,
                height,
                overflowY: 'scroll',
                overflowX: 'hidden',
                backgroundColor: backgroundColor || colors.gray['100'],
                paddingBottom: isMobile ? 75 : 0,
              }}
            >
              {headerRender()}
              <div
                className={enableScrollBar ? '' : 'no-scrollbar'}
                style={{
                  width,
                  fontFamily:
                    'ui-sans-serif,system-ui,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji',
                  // overflowY: 'auto',
                }}
                ref={contentRef}
              >
                <Responsive
                  className="layout"
                  layouts={layouts}
                  onBreakpointChange={onBreakpointChange}
                  onLayoutChange={onLayoutChange}
                  width={width}
                  useCSSTransforms={mounted}
                  {...gridProps}
                  isDraggable={constants.DASHBOARD_EDIT}
                  isResizable={constants.DASHBOARD_EDIT}
                  isBounded={false}
                  isDroppable={false}
                >
                  {MemorizedCards}
                </Responsive>
              </div>
            </div>
          )}
        </AutoSizer>
      </Content>
    );
  }, [rest, cards, isMobile, isPresenting]);

  return (
    <Container
      id="DashContainer"
      menuWidth={useDashMenu?.state?.show ? 250 : 30}
    >
      <DashMenu
        options={dashMenuOptions || []}
        dashMenuPath={dashMenuPath}
        useDashMenu={useDashMenu}
        hiddenSupergroup={hiddenSupergroup}
        openSelectSuperGroup={openSelectSuperGroup}
        isMobile={isMobile}
      />
      {dashRender()}
    </Container>
  );
};

export default MainScreen;
